import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, OnInit, Optional, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { IPSAuthenticationService, IPSObservabilityService } from '@ips/common-angular';
import {
  DocumentItemList1,
  DocumentService,
} from 'com.trumbull.phx.document.api-client';
import { OrderService, OrderUpdate } from 'com.trumbull.phx.order.api-client';
import { RecordRetrievalItemService, RecordRetrievalRequestItem } from 'com.trumbull.phx.recordretrievalobjective.api-client';
import { CaseFileItem, CaseFileItemService } from 'com.trumbull.phx.casefile.api-client';
import { WorkItemList, WorkService } from 'com.trumbull.phx.work.api-client';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import {
  catchError,
  combineLatest,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap
} from 'rxjs';
import { DocumentProcessingService } from '../../services/document-processor/document-processor.service';
import { OrderDetailsMapperService } from '../../services/order-details-mapper/order-details-mapper.service';
import { SharedService } from '../../services/shared.service';
import { ModalComponent } from '../../shared/modal/modal.component';
import { NeedHelpModalComponent } from '../../shared/modal/need-help-modal/need-help-modal.component';
import { AddProviderComponent } from './add-provider/add-provider.component';
import { OrderDetailsGeneralInformationComponent } from './order-details-general-information/order-details-general-information.component';
import { OrderDetails } from './order-details.model';
import { RootObject } from './orders.model';
import {
  DocumentReference,
  RecordIndexTableComponent,
  TableData,
} from './record-index-table/record-index-table.component';
import { VocabHelperService } from '../../services/vocab-helper/vocab-helper.service';
import { UserHelperService } from '../../services/user-helper/user-helper.service';
import { CommentListService } from 'com.trumbull.phx.commentlist.api-client';
import { AttachmentProcessorService } from '../place-order/attachment.processor.service';
import { OrderHydrationService } from '../../services/order-hydration/order-hydration.service';

@Component({
  selector: 'app-order-details',
  imports: [
    PanelModule,
    TabsModule,
    ButtonModule,
    OrderDetailsGeneralInformationComponent,
    ModalComponent,
    ButtonModule,
    RecordIndexTableComponent,
    NeedHelpModalComponent,
    AddProviderComponent,
    CommonModule,
    ToastModule
  ],
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss'],
  providers: [DatePipe, MessageService],
})
export class OrderDetailsComponent implements OnInit {
  @ViewChild(RecordIndexTableComponent)
  recordIndexTableComponent!: RecordIndexTableComponent;
  @ViewChild('detailsHeading') detailsHeading!: ElementRef;

  orderDetails?: OrderDetails;
  orderData?: RootObject;
  orderDocuments?: (DocumentItemList1 | undefined)[];
  showHelpModal: boolean = false;
  orderId$: Observable<string | null>;
  orderNotFound: boolean = false;

  recordRequests: TableData[] = [];

  isOrderLoading = false;
  isDocumentsLoading = false;
  isUserLinkedToAccount: boolean = false;
  isAddProviderClicked: boolean = false;
  orderUpdateData?: OrderUpdate;
  requestDocuments: any[] = [];
  vocabDocumentTagsData: any = [];
  showContent = false;

  constructor(
    private readonly orderService: OrderService,
    private readonly recordRetrievalObjectiveService: RecordRetrievalItemService,
    private readonly caseFileItemService: CaseFileItemService,
    private readonly workService: WorkService,
    private readonly documentService: DocumentService,
    private readonly documentProcessor: DocumentProcessingService,
    private readonly orderDetailsMapperService: OrderDetailsMapperService,
    private route: ActivatedRoute,
    private router: Router,
    private vocabHelperService: VocabHelperService,
    private messageService: MessageService,
    @Optional() private observability: IPSObservabilityService,
    private readonly userService: UserHelperService,
    private readonly authService: IPSAuthenticationService,
    private readonly sharedService: SharedService,
    private readonly commentListService: CommentListService,
    private attachmentProcessorService: AttachmentProcessorService,
    private readonly orderHydrationService: OrderHydrationService
  ) {
    this.orderId$ = this.route.paramMap.pipe(
      switchMap((params: ParamMap) => {
        return new Observable<string | null>((observer) => {
          observer.next(params.get('id'));
          observer.complete();
        });
      })
    );
  }

  ngOnInit(): void {
    this.setBreadcrumbPath();
    this.subscribeToCurrentUser();
    this.onInitOrderDetails(false);
  }

  // add focus at the top section
  ngAfterViewInit() {
    setTimeout(() => {
      this.detailsHeading.nativeElement.focus();
    });
    // added delay for smooth navigation when using screen reader
    setTimeout(() => {
      this.showContent = true;
    },20)
  }

  onInitOrderDetails(isProviderAdded: boolean) {
    if (isProviderAdded) {
      this.messageService.add({
        severity: 'success',
        summary: 'SUCCESS',
        detail: `Request successfully added to the order ${this.orderDetails?.id}.`,
        life: 3000,
      });
    }

    this.orderId$
      .pipe(
        filter((id) => !!id),
        switchMap((id: string | null) => {
          this.isOrderLoading = true;
          // this.isDocumentsLoading = true;

          if (!id) {
            return of(null);
          }

          const hydration$ = this.orderHydrationService.hydrateOrderWithCaseAndRetrieval(id, '', 0, 100);

          const vocabData$ = this.vocabHelperService
            .getVocabData([
              'OrderType',
              'ContactTitle',
              'PhysicalLocationUsAddressStateCode',
              'RecordTypes',
              'VA_Options',
              'UserOrganization',
              'RecordRetrievalCustomerCode',
              'OrderStatusCode',
              'DocumentTypeCode',
              'RecordRetrievalRequestStatusCode',
              'OrderDocumentCode',
              'RequestDocumentCode'
            ])
            .pipe(
              catchError((error) => {
                console.error('Error fetching vocab data:', error);
                return of(null);
              })
            );

          const userData$ = this.userService
            .getUsersByAttribute(undefined, undefined)
            .pipe(
              catchError((error) => {
                console.error('Error fetching user data:', error);
                return of(null);
              })
            );

          // Combine hydration, vocab, and user data first
          return combineLatest([hydration$, vocabData$, userData$]).pipe(
            switchMap(([hydration, vocabData, userData]) => {
              if (vocabData) {
                this.vocabDocumentTagsData = vocabData?.filter((d: any) => d.vocabularyName === 'OrderDocumentCode' || d.vocabularyName === 'RequestDocumentCode');
              }
              const order = hydration?.order;
              const details = hydration?.orderDetails;
              const work = hydration?.work as WorkItemList | undefined;
              if (order && details) {
                this.orderData = details as RootObject;
                this.orderUpdateData = {
                  customerReferenceNumber: order?.customerReferenceNumber,
                  qualifiers: order?.qualifiers,
                  summaryDescription: order?.summaryDescription,
                  orderedBy: order?.orderedBy,
                };
                // this.orderDetails = this.orderDetailsMapperService.mapOrderDetails(
                //   order,
                //   details as RootObject,
                //   vocabData,
                //   userData?.users
                // );
                // if (order) {
                //   this.recordRequests = this.documentProcessor.mapRequests(
                //     order,
                //     details as RootObject,
                //     undefined,
                //     vocabData
                //   );
                // }
                // this.isOrderLoading = false;
              }

              if (work?.workItems?.length && order && hydration?.caseData && hydration?.recordRetrievalData) {
                this.isDocumentsLoading = true;
                const commentList$ = this.getStatusInfoMap(work);
                return commentList$.pipe(
                  switchMap((commentList) => {
                    const recordRetrievalData = hydration.recordRetrievalData as {
                      requests?: any[];
                      documentLists?: any[];
                    };
                    this.orderData = {
                      case: hydration.caseData,
                      retrievalObjective: recordRetrievalData,
                      attachments: []
                    } as RootObject;

                    this.orderDetails = this.orderDetailsMapperService.mapOrderDetails(
                      order,
                      this.orderData,
                      vocabData,
                      userData
                    )

                    this.recordRequests = this.documentProcessor.mapRequests(
                      order,
                      this.orderData,
                      undefined,
                      vocabData,
                      commentList
                    );

                    this.isOrderLoading = false;

                    // Fetch documents for the record retrieval objective
                    let rrDocuments: any = [];
                    recordRetrievalData.requests?.forEach((request: any) => {
                      if (Array.isArray(request?.documentLists)) {
                        rrDocuments.push(...request.documentLists);
                      }
                    });
                    if (Array.isArray(recordRetrievalData.documentLists)) {
                      rrDocuments.push(...recordRetrievalData.documentLists);
                    }

                    this.documentProcessor
                      .fetchDocuments2(
                        rrDocuments
                          ?.flatMap((item:any) => item.documentListId)
                          .filter((id:any): id is string => id !== undefined) || [],
                        (loading) => (this.isDocumentsLoading = loading)
                      )
                      .subscribe((documents) => {
                        this.orderDocuments = documents;
                        this.attachmentProcessorService.setAllDocuments(this.orderDocuments);
                        const documentListPattern = /^(\d+(?:-\d+)*)\s*-\s*/;
                        const processedDocs =
                          this.documentProcessor.filterDocumentReferences(
                            this.orderDocuments || [],
                            documentListPattern
                          );
                        this.recordRequests = this.documentProcessor.mapRequests(
                          order,
                          this.orderData as RootObject,
                          documents,
                          vocabData,
                          commentList
                        );
                        this.getRequestDocuments(recordRetrievalData.requests as RecordRetrievalRequestItem[] | undefined);
                        this.observability?.trackEvent(
                          'document:processing:filter:results',
                          processedDocs.stats
                        );
                      });
                    return of(null);
                  }),
                );
                //

                // return this.recordRetrievalObjectiveService
                //   .getRecordRetrievalObjective(objectiveId, 'standard')
                //   .pipe(
                //     tap((recordRetrieval) => {
                //       if (recordRetrieval) {
                //         this.documentProcessor
                //           .fetchDocuments2(
                //             recordRetrieval.documentLists
                //               ?.flatMap((item) => item.documentListId)
                //               .filter((id): id is string => id !== undefined) || [],
                //             (loading) => (this.isDocumentsLoading = loading)
                //           )
                //           .subscribe((documents) => {
                //             this.orderDocuments = documents;
                //             const documentListPattern = /^(\d+) - [A-Za-z\d\s-]+ - [A-Za-z\d]+$/;
                //             const processedDocs =
                //               this.documentProcessor.filterDocumentReferences(
                //                 this.orderDocuments || [],
                //                 documentListPattern
                //               );
                //             this.recordRequests = this.documentProcessor.mapRequests(
                //               order,
                //               details as RootObject,
                //               documents,
                //               vocabData
                //             );

                //             this.observability?.trackEvent(
                //               'document:processing:filter:results',
                //               processedDocs.stats
                //             );
                //           });
                //       }
                //     }),
                //     catchError((error) => {
                //       console.error('Error fetching record retrieval objective:', error);
                //       this.isDocumentsLoading = false;
                //       return of(null);
                //     }),
                //   );
              }
              return of(null);
            }),
          );
        })
      )
      .subscribe();
  }

  getRequestDocuments(requests: RecordRetrievalRequestItem[] | undefined) {
    this.attachmentProcessorService.getMatchedDocumentsForRequests(requests ?? [], this.vocabDocumentTagsData.find((d: any) => d.vocabularyName === 'RequestDocumentCode'))
      .subscribe(documents => {
        this.requestDocuments = documents;
      });
  }

  getStatusInfoMap(work: WorkItemList): Observable<Record<string, { statusMessage: string, statusUpdatedDate: string }>> {
    const stories = work?.workItems?.[0]?.stories || [];

    if (stories.length === 0) {
      return of({});
    }

    const observables = stories.map(story => {
      const commentListId = story.commentLists?.[0].commentListId || '';
      const storyKey = 'RR-' + story.scope?.[0].foreignEntityId;

      return this.commentListService.getCommentList('standard', commentListId).pipe(
        map(response => {
          let latest;
          if (response?.comments?.length) {
            latest = response.comments
              .filter(comment =>
                comment.visibility?.toLowerCase() === 'public' &&
                comment.categoryCode?.toLowerCase() === 'client' &&
                !!comment.createdDate
              )
              .sort((a, b) =>
                new Date(b.createdDate ?? '').getTime() - new Date(a.createdDate ?? '').getTime()
              )[0];
          }

          return {
            storyKey,
            statusMessage: latest?.text || '',
            statusUpdatedDate: latest?.createdDate || ''
          };
        })
      );
    });

    return forkJoin(observables).pipe(
      map(results =>
        results.reduce((acc, item) => {
          acc[(item.storyKey ?? 0)] = {
            statusMessage: item.statusMessage,
            statusUpdatedDate: item.statusUpdatedDate
          };
          return acc;
        }, {} as Record<string, { statusMessage: string; statusUpdatedDate: string }>)
      )
    );
  }

  needHelp() {
    this.showHelpModal = true;
  }

  onAddProvider() {
    this.isAddProviderClicked = false;
  }

  editOrder(orderId: any) {
    this.router.navigate(['/home/edit-order/', orderId]);
  }

  tableAction(data: TableEvent | any) {
    let type = data?.action || data;
    switch (type) {
      case 'expandAll':
        this.recordIndexTableComponent.expandAll();
        break;
      case 'collapseAll':
        this.recordIndexTableComponent.collapseAll();
        break;
      case 'downloadAll':
        break;
      case 'pageChange':
        break;
      case 'downloadSingleFile':
        if (!data || typeof data !== 'object' || !('action' in data)) {
          return;
        }

        this.documentService
          .getDocumentContent(
            'standard',
            (data.data as DocumentReference)?.documentListId || '',
            (data.data as DocumentReference)?.documentId || ''
          )
          .subscribe((response) => {
            if (response) {
              this.downloadFile(response, data);
            }
          });

        break;
      case 'downloadMultipleFiles':
        if (data?.data?.documentListId) {
          this.documentService
            .getDocumentContentZip('standard', data?.data?.documentListId || '')
            .subscribe((response) => {
              this.downloadFile(response, data, true);
            });
        }
        break;
      case 'downloadAllFiles':
        let documentListIds = new Set();

        data.data.forEach((doc: any) => {
          doc.documents.forEach((d: any) => {
            documentListIds.add(d.documentListId);
          });
        });

        documentListIds.forEach((documentListId: any) => {
          this.documentService
            .getDocumentContentZip('standard', documentListId || '')
            .subscribe((response) => {
              this.downloadFile(response, data, true);
            });
        });

        break;
      default:
        throw new Error(`Unknown action: ${type}`);
    }
  }

  private downloadFile(response: Blob, data: any, archive = false) {
    const blob = new Blob([response as any], {
      type: 'application/octet-stream',
    });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = archive
      ? `download.zip`
      : (data.data as DocumentReference)?.fileName || 'document';
    document.body.appendChild(a);

    a.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  downloadAll() {
    this.tableAction({ action: 'downloadAllFiles', data: this.recordRequests });
  }

  hasDocs() {
    return this.recordRequests?.some(
      (request) => request.documents && request.documents.length > 0
    );
  }

  subscribeToCurrentUser() {
    this.authService.currentUser.forEach(user => {
      let organizationValue: any = user?.definition?.attributes?.find(item => item?.key === 'organization');
      if (organizationValue?.value) {
        this.isUserLinkedToAccount = true;
      } else {
        this.isUserLinkedToAccount = false;
      }
    });
  }

  setBreadcrumbPath() {
    this.sharedService.updateBreadcrumbPath([
      { label: 'Order Details' }
    ]);
  }
}

interface TableEvent {
  action: string;
  data: DocumentReference | DocumentReference[] | any;
}
